// =============================================================================
// STREETSTICKER.COM — EGGSHELL STICKER PRICING ENGINE (US MARKET)
// =============================================================================
// All costs are included in the price: printing, production, shipping and fees.
//
//   linearPerUnit = (SETUP_PER_STICKER + BASE_PER_IN2 * area_in2) * shapeMult
//   unitBase      = max(MIN_UNIT_PRICE, linearPerUnit) * PRICE_MULTIPLIER
//   unitFinal     = unitBase * (1 - quantityDiscount)
//   total         = max(MIN_ORDER_TOTAL, unitFinal * qty), rounded to $1
//
// PRICE_MULTIPLIER raises or lowers every price on the site at once
// (configurator, price table and homepage packs all read from here).
// Do not also use a Markets catalog percentage: the configurator product is
// priced at $0.01 per unit, so a catalog percentage would not apply to it.
// =============================================================================

const PRICING = {
  BASE_PER_IN2:      0.045,  // USD per in² (material + ink)
  SETUP_PER_STICKER: 0.15,   // USD per-sticker setup base (print, die-cut, handling)
  MIN_UNIT_PRICE:    0.10,   // Hard floor per sticker
  PRICE_MULTIPLIER:  1.30,   // Global price factor (Sep 2026). See header.
  MIN_ORDER_TOTAL:   120,    // Minimum order value in USD
  TIER_STEP_UP:      15,     // Each tier must be at least $X above the previous
                             // (only kicks in when the previous tier was already
                             // sitting on a floor — keeps the staffel monotone).

  // Shape multiplier — angular shapes are more expensive to cut/produce.
  SHAPE_MULTIPLIER: {
    square:    1.13,
    rectangle: 1.15,
    round:     1.00
  },

  // Volume tiers. `discount` is the reduction off the list price per sticker.
  // What the customer sees as "save X %" is always measured against the
  // smallest tier (500), never against a list price nobody can buy.
  // 250 was dropped in Sep 2026.
  QUANTITY_TIERS: [
    { qty: 500,   discount: 0.18 },
    { qty: 1000,  discount: 0.30 },
    { qty: 2000,  discount: 0.42 },
    { qty: 5000,  discount: 0.54 },
    { qty: 7500,  discount: 0.58 },
    { qty: 10000, discount: 0.62 }
  ],

  // Tier highlighted in the configurator as the most ordered quantity.
  POPULAR_QTY: 1000,

  SHAPES: {
    square: {
      label: 'Square',
      icon: 'square',
      sizes: [
        { id: 'sq-2', w: 2, h: 2, label: '2 × 2 in' },
        { id: 'sq-3', w: 3, h: 3, label: '3 × 3 in' },
        { id: 'sq-4', w: 4, h: 4, label: '4 × 4 in' },
        { id: 'sq-5', w: 5, h: 5, label: '5 × 5 in' }
      ]
    },
    rectangle: {
      label: 'Rectangle',
      icon: 'rectangle',
      sizes: [
        { id: 'rc-2x3', w: 2, h: 3, label: '2 × 3 in' },
        { id: 'rc-3x4', w: 3, h: 4, label: '3 × 4 in' },
        { id: 'rc-2x5', w: 2, h: 5, label: '2 × 5 in' },
        { id: 'rc-2x8', w: 2, h: 8, label: '2 × 8 in' }
      ]
    },
    round: {
      label: 'Round',
      icon: 'circle',
      sizes: [
        { id: 'rd-2', w: 2, h: 2, label: '2 in' },
        { id: 'rd-3', w: 3, h: 3, label: '3 in' },
        { id: 'rd-4', w: 4, h: 4, label: '4 in' },
        { id: 'rd-5', w: 5, h: 5, label: '5 in' }
      ]
    }
  },

  CUSTOM_MIN_IN: 1,
  CUSTOM_MAX_IN: 36
};

// -----------------------------------------------------------------------------
// AREA (in²)
// -----------------------------------------------------------------------------
function calcArea(shape, w, h) {
  if (shape === 'round') {
    const r = w / 2;
    return Math.PI * r * r;
  }
  return w * h;
}

// -----------------------------------------------------------------------------
// CORE PRICE
// -----------------------------------------------------------------------------
function priceFor(areaIn2, qty, shape) {
  const tier = PRICING.QUANTITY_TIERS.find(t => t.qty === qty) || closestTier(qty);
  const shapeMult = (shape && PRICING.SHAPE_MULTIPLIER[shape]) || 1.0;

  const linearPerUnit = (PRICING.SETUP_PER_STICKER + PRICING.BASE_PER_IN2 * areaIn2) * shapeMult;
  const basePerUnit = Math.max(PRICING.MIN_UNIT_PRICE, linearPerUnit) * (PRICING.PRICE_MULTIPLIER || 1);
  const discountedPerUnit = basePerUnit * (1 - tier.discount);

  const rawTotal = discountedPerUnit * qty;

  // Hard floor 1: minimum order value
  const minimumApplied = rawTotal < PRICING.MIN_ORDER_TOTAL;
  let totalUnrounded = minimumApplied ? PRICING.MIN_ORDER_TOTAL : rawTotal;
  let tieredStepApplied = false;

  // Hard floor 2: each tier must be at least TIER_STEP_UP above the previous,
  // but only if the previous tier was already on a floor.
  const tierIdx = PRICING.QUANTITY_TIERS.findIndex(t => t.qty === qty);
  if (tierIdx > 0 && PRICING.TIER_STEP_UP > 0) {
    const prevTier = PRICING.QUANTITY_TIERS[tierIdx - 1];
    const prev = priceFor(areaIn2, prevTier.qty, shape);
    if (prev.minimumApplied || prev.tieredStepApplied) {
      const stepFloor = prev.total + PRICING.TIER_STEP_UP;
      if (totalUnrounded < stepFloor) {
        totalUnrounded = stepFloor;
        tieredStepApplied = true;
      }
    }
  }

  // ROUND TOTAL TO WHOLE DOLLAR (US market preference)
  const total = Math.round(totalUnrounded);
  const effectivePerUnit = total / qty;

  return {
    qty,
    areaIn2,
    basePerUnit,
    discountedPerUnit: effectivePerUnit,
    discount: tier.discount,
    total,
    minimumApplied,
    tieredStepApplied,
    floorActive: minimumApplied || tieredStepApplied
  };
}

function closestTier(qty) {
  return PRICING.QUANTITY_TIERS.reduce((a, b) =>
    Math.abs(b.qty - qty) < Math.abs(a.qty - qty) ? b : a
  );
}

// -----------------------------------------------------------------------------
// SAVINGS vs. smallest tier
// -----------------------------------------------------------------------------
function calcSavings(areaIn2, qty, shape) {
  const ref = priceFor(areaIn2, PRICING.QUANTITY_TIERS[0].qty, shape);
  const cur = priceFor(areaIn2, qty, shape);

  const referencePerUnit = ref.discountedPerUnit;
  const currentPerUnit   = cur.discountedPerUnit;

  const wouldHaveCost = referencePerUnit * qty;
  const savedAbsolute = Math.max(0, wouldHaveCost - cur.total);
  const savedPercent  = referencePerUnit > 0 && currentPerUnit < referencePerUnit
    ? ((referencePerUnit - currentPerUnit) / referencePerUnit) * 100
    : 0;

  return {
    referencePerUnit,
    currentPerUnit,
    savedAbsolute,
    savedPercent
  };
}

// -----------------------------------------------------------------------------
// SHOPIFY VARIANT KEY
// -----------------------------------------------------------------------------
// Standard sizes exist as real Shopify variants (options: Shape / Size /
// Quantity). The key must match the option values exactly, e.g.
// "Square|3 x 3 in|1,000 pcs". Returns null for custom sizes.
function variantKey(shape, w, h, qty) {
  const def = PRICING.SHAPES[shape];
  if (!def) return null;
  const std = def.sizes.find(s => s.w === w && s.h === h);
  if (!std) return null;
  return def.label + '|' + std.label.replace('×', 'x') + '|' + qty.toLocaleString('en-US') + ' pcs';
}

// -----------------------------------------------------------------------------
// SHIPPING WEIGHT ESTIMATE (grams)
// -----------------------------------------------------------------------------
// Film + release liner approx. 220 g/m2, plus 10 % cutting margin and about
// 80 g of packaging. A rough estimate to pick the parcel service and prefill
// the shipping label.
const WEIGHT = { G_PER_M2: 220, MARGIN: 1.10, PACKAGING_G: 80 };
function estimateWeightGrams(shape, w, h, qty) {
  const boxIn2 = w * h;                       // round stickers are cut from a square
  const m2 = boxIn2 * 0.00064516 * qty * WEIGHT.MARGIN;
  return Math.round(m2 * WEIGHT.G_PER_M2 + WEIGHT.PACKAGING_G);
}

// -----------------------------------------------------------------------------
// FORMATTERS
// -----------------------------------------------------------------------------
// Whole-dollar formatter for totals: "$261"
function fmtUSD(n) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0
  }).format(Math.round(n));
}

// Cents formatter for per-unit prices: "$0.26"
function fmtUSDcents(n) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 2, maximumFractionDigits: 2
  }).format(n);
}

if (typeof module !== 'undefined') {
  module.exports = { PRICING, calcArea, priceFor, calcSavings, variantKey, estimateWeightGrams, fmtUSD, fmtUSDcents };
}
